import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
const MIXPANEL_ENABLED = import.meta.env.VITE_ENABLE_MIXPANEL !== 'false';

let isInitialized = false;

export const analytics = {
  /**
   * Initialize Mixpanel with the configured token
   * Should be called once at app startup
   */
  init: () => {
    if (MIXPANEL_ENABLED && MIXPANEL_TOKEN && !isInitialized) {
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: import.meta.env.DEV,
        autocapture: true,
        record_sessions_percent: 100,
        api_host: 'https://api-eu.mixpanel.com',
        track_pageview: false, // We track manually for more control
        persistence: 'localStorage',
        ignore_dnt: false, // Respect Do Not Track
      });
      isInitialized = true;
    }
  },

  /**
   * Identify a user after login
   * @param userId - The user's unique ID
   * @param traits - Additional user properties to set
   */
  identify: (userId: string, traits?: Record<string, unknown>) => {
    if (MIXPANEL_ENABLED && MIXPANEL_TOKEN &&isInitialized) {
      mixpanel.identify(userId);
      if (traits !== undefined) {
        mixpanel.people.set(traits);
      }
    }
  },

  /**
   * Reset user identity on logout
   */
  reset: () => {
    if (MIXPANEL_ENABLED && MIXPANEL_TOKEN &&isInitialized) {
      mixpanel.reset();
    }
  },

  /**
   * Disable tracking and clear all Mixpanel data
   * Used when user revokes analytics consent
   */
  disable: () => {
    if (MIXPANEL_ENABLED && MIXPANEL_TOKEN &&isInitialized) {
      mixpanel.opt_out_tracking();
      mixpanel.reset();
      isInitialized = false;
    }
  },

  /**
   * Track a custom event
   * @param event - The event name
   * @param properties - Additional event properties
   */
  track: (event: string, properties?: Record<string, unknown>) => {
    if (MIXPANEL_ENABLED && MIXPANEL_TOKEN &&isInitialized) {
      mixpanel.track(event, properties);
    }
  },

  /**
   * Track a page view
   * @param pageName - The page name/path
   * @param properties - Additional properties
   */
  pageView: (pageName: string, properties?: Record<string, unknown>) => {
    if (MIXPANEL_ENABLED && MIXPANEL_TOKEN &&isInitialized) {
      mixpanel.track('Page View', {
        page: pageName,
        page_path: pageName,
        page_title: document.title,
        $current_url: window.location.href,
        ...properties
      });
    }
  },

  /**
   * Set a user property
   * @param properties - Properties to set on the user profile
   */
  setUserProperties: (properties: Record<string, unknown>) => {
    if (MIXPANEL_ENABLED && MIXPANEL_TOKEN &&isInitialized) {
      mixpanel.people.set(properties);
    }
  },

  /**
   * Increment a numeric user property
   * @param property - The property name
   * @param value - The value to increment by (default: 1)
   */
  incrementUserProperty: (property: string, value: number = 1) => {
    if (MIXPANEL_ENABLED && MIXPANEL_TOKEN &&isInitialized) {
      mixpanel.people.increment(property, value);
    }
  },
};

// Predefined event names for consistency
export const AnalyticsEvents = {
  // Auth events
  LOGIN: 'Login',
  LOGOUT: 'Logout',

  // Chat events
  CHAT_STARTED: 'Chat Started',
  CHAT_MESSAGE_SENT: 'Chat Message Sent',

  // Video events
  VIDEO_WATCHED: 'Video Watched',
  VIDEO_COMPLETED: 'Video Completed',

  // Course events
  COURSE_ENROLLED: 'Course Enrolled',
  MODULE_COMPLETED: 'Module Completed',

  // Trading events
  TRADE_VIEWED: 'Trade Viewed',
  TRADE_RECOMMENDATION_CLICKED: 'Trade Recommendation Clicked',

  // Navigation events
  PAGE_VIEW: 'Page View',

  // Checkout events
  CHECKOUT_STARTED: 'Checkout Started',
  CHECKOUT_COMPLETED: 'Checkout Completed',
  CHECKOUT_CANCELLED: 'Checkout Cancelled',

  // Landing / Registration funnel events
  PLAN_SELECTED: 'Plan Selected',
  REGISTRATION_STARTED: 'Registration Started',
  REGISTRATION_ACCOUNT_SUBMITTED: 'Registration Account Submitted',
  REGISTRATION_CODE_SUBMITTED: 'Registration Code Submitted',
  REGISTRATION_COMPLETED: 'Registration Completed',
  REGISTRATION_RESEND_CODE: 'Registration Resend Code',

  // DayTrading landing events
  DAYTRADING_CALCULATOR_CHANGED: 'DayTrading Calculator Changed',
} as const;
